/**
 * Adaptive quality. The single most important file for a 3D game that has to run on both a desktop
 * GPU and a three-year-old phone.
 *
 * The rule: start at the tier the device claims it can handle, then MEASURE and drop if the frame
 * budget is missed. Never trust a static device check alone — thermal throttling makes a phone that
 * was fast for ten seconds slow for the next ten, and a game that ignores that stutters exactly when
 * the player is doing well.
 */

export type QualityTier = 'high' | 'medium' | 'low';

export interface QualitySettings {
	/** Bloom and other post-processing. First thing to go — it is the most expensive per pixel. */
	postProcessing: boolean;
	/** Real shadow maps. Second to go; the fake contact shadow stays at every tier. */
	shadows: boolean;
	shadowMapSize: number;
	/** Renderer pixel ratio ceiling. 2 is retina; 1 halves the pixels the GPU touches. */
	maxPixelRatio: number;
	/** Anti-aliasing is free on desktop, expensive on mobile with post-processing. */
	antialias: boolean;
	/** Scenery density — fewer props at lower tiers. */
	sceneryCount: number;
	particleCount: number;
}

const SETTINGS: Record<QualityTier, QualitySettings> = {
	high: { postProcessing: true, shadows: true, shadowMapSize: 1024, maxPixelRatio: 2, antialias: true, sceneryCount: 26, particleCount: 140 },
	medium: { postProcessing: true, shadows: false, shadowMapSize: 512, maxPixelRatio: 1.5, antialias: true, sceneryCount: 18, particleCount: 90 },
	low: { postProcessing: false, shadows: false, shadowMapSize: 0, maxPixelRatio: 1, antialias: false, sceneryCount: 10, particleCount: 50 },
};

/**
 * Best guess before a single frame has been drawn.
 *
 * Deliberately conservative on mobile: it is far better to start medium and be promoted by good
 * measurements than to start high, drop frames for two seconds, and have the player's first
 * impression be a stutter.
 */
export function detectTier(): QualityTier {
	const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
	const cores = navigator.hardwareConcurrency ?? 4;
	const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

	if (isMobile) return cores >= 6 && memory >= 4 ? 'medium' : 'low';
	return cores >= 8 ? 'high' : 'medium';
}

export const settingsFor = (tier: QualityTier): QualitySettings => SETTINGS[tier];

/**
 * Watches the real frame rate and reports when the tier should change.
 *
 * Hysteresis matters here: dropping the moment one frame is slow would toggle bloom on and off
 * every second, which looks far worse than either state. It only acts on a sustained window, and it
 * demotes readily but promotes reluctantly.
 */
export class QualityGovernor {
	private frames = 0;
	private windowStart = 0;
	private sustainedGood = 0;

	constructor(
		private tier: QualityTier,
		private readonly onChange: (tier: QualityTier) => void,
	) {}

	/** Call once per rendered frame. */
	sample(now: number): void {
		if (this.windowStart === 0) this.windowStart = now;
		this.frames++;

		const elapsed = now - this.windowStart;
		if (elapsed < 1000) return;

		const fps = (this.frames * 1000) / elapsed;
		this.frames = 0;
		this.windowStart = now;

		// Demote immediately on a bad second — the player is feeling it right now.
		if (fps < 45 && this.tier !== 'low') {
			this.sustainedGood = 0;
			this.tier = this.tier === 'high' ? 'medium' : 'low';
			this.onChange(this.tier);
			return;
		}

		// Promote only after several consecutive good seconds, so a brief lull does not oscillate.
		if (fps > 58 && this.tier !== 'high') {
			this.sustainedGood++;
			if (this.sustainedGood >= 5) {
				this.sustainedGood = 0;
				this.tier = this.tier === 'low' ? 'medium' : 'high';
				this.onChange(this.tier);
			}
			return;
		}

		this.sustainedGood = 0;
	}

	current(): QualityTier {
		return this.tier;
	}
}
